/**
 * UnifiedFilterBar [T82P1]
 *
 * A feature-rich filter bar that owns period/date-range selection plus all
 * advanced transaction filters.  It replaces the old TransactionFilters
 * component and also removes the period picker from InsightsCard.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Input } from '@/components/ui/input';
// [T-WP2] Date calendar + range popover extracted to TransactionDateRangeControl.
import { TransactionDateRangeControl } from '@/components/transactions/TransactionDateRangeControl';
// Popover is still used for the saved-presets popover (not for date range).

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  ChevronDown,
  ChevronUp,
  X,
  Star,
  CircleHelp,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { TagInput } from '@/components/ui/tag-input';

import type { Account, Category } from '@/lib/types';
import { PERIOD_LABELS, type FilterState, type FilterPreset } from '@/lib/filters';
// [T270-PY] Re-export canonical FilterState + FilterPreset + getDateRangeFromFilter from
// @/lib/filters to eliminate the BLOCKER-3 duplication smell called out in the T270 contract
// preflight (§3.3). Keeping these as re-exports preserves the public surface that other
// consumers import from this module so we do not break their callers during the refactor.
export { getDateRangeFromFilter } from '@/lib/filters';
export type { FilterState, FilterPreset };

/** Return a list of removable chips describing every active filter. */
export function buildActiveChips(
  filters: FilterState,
  accounts: Account[],
  categories: Category[],
): { key: string; label: string }[] {
  const chips: { key: string; label: string }[] = [];
  if (filters.search) chips.push({ key: 'search', label: `"${filters.search}"` });
  if (filters.type) chips.push({ key: 'type', label: `Type: ${filters.type}` });
  filters.accountIds?.forEach((id) => {
    const acc = accounts.find((a) => a.id === id);
    chips.push({ key: `account:${id}`, label: acc ? acc.name : `Account: ${id}` });
  });
  filters.categoryIds?.forEach((id) => {
    const cat = categories.find((c) => c.id === id);
    const label = cat ? (cat.emoji ? `${cat.emoji} ${cat.name}` : cat.name) : `Category: ${id}`;
    chips.push({ key: `category:${id}`, label });
  });
  if (filters.amountMin != null) chips.push({ key: 'amountMin', label: `≥ ${filters.amountMin}` });
  if (filters.amountMax != null) chips.push({ key: 'amountMax', label: `≤ ${filters.amountMax}` });
  filters.tags?.forEach((tag) => chips.push({ key: `tag:${tag}`, label: `#${tag}` }));
  if (filters.isRecurring) chips.push({ key: 'isRecurring', label: 'Recurring only' });
  if (filters.status) chips.push({ key: 'status', label: `Status: ${filters.status}` });
  return chips;
}

/** Remove the filter corresponding to a chip key and call onChange. */
export function removeChip(
  key: string,
  filters: FilterState,
  onChange: (f: FilterState) => void,
): void {
  const next = { ...filters };
  if (key === 'search') delete next.search;
  else if (key === 'type') delete next.type;
  else if (key.startsWith('account:')) {
    const id = key.slice(8);
    next.accountIds = (next.accountIds ?? []).filter((a) => a !== id);
    if (!next.accountIds.length) delete next.accountIds;
  } else if (key.startsWith('category:')) {
    const id = key.slice(9);
    next.categoryIds = (next.categoryIds ?? []).filter((c) => c !== id);
    if (!next.categoryIds.length) delete next.categoryIds;
  } else if (key === 'amountMin') delete next.amountMin;
  else if (key === 'amountMax') delete next.amountMax;
  else if (key.startsWith('tag:')) {
    const tag = key.slice(4);
    next.tags = (next.tags ?? []).filter((t) => t !== tag);
    if (!next.tags.length) delete next.tags;
  } else if (key === 'isRecurring') delete next.isRecurring;
  else if (key === 'status') delete next.status;
  onChange(next);
}

/** Map a chip key to the advanced filter field name it corresponds to. */
function chipKeyToAdvancedField(key: string): string | null {
  if (key === 'search') return 'search';
  if (key === 'type') return 'type';
  if (key.startsWith('account:')) return 'account';
  if (key.startsWith('category:')) return 'category';
  if (key === 'amountMin' || key === 'amountMax') return 'amount';
  if (key.startsWith('tag:')) return 'tags';
  if (key === 'isRecurring') return 'recurring';
  if (key === 'status') return 'status';
  return null;
}

// ─── Internal constants ────────────────────────────────────────────────────────

const MAX_PRESETS = 5;

// [T-WP2] The local `PERIODS` array (which extended CANONICAL_PERIODS with `7d`/`30d`/`custom` for the inline period pills) is no longer used here — the unified TransactionDateRangeControl owns its own preset list. Removed to avoid a dead const.

const PRESET_STORAGE_KEY = 'filterPresets';

function loadPresets(): FilterPreset[] {
  try {
    return JSON.parse(localStorage.getItem(PRESET_STORAGE_KEY) ?? '[]') as FilterPreset[];
  } catch {
    return [];
  }
}

function savePresets(presets: FilterPreset[]): void {
  localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets));
}

// ─── Props ─────────────────────────────────────────────────────────────────────

type UnifiedFilterBarPresentation = 'full' | 'minimized-inline';

interface UnifiedFilterBarProps {
  filters: FilterState;
  onFiltersChange: (f: FilterState) => void;
  onClear: () => void;
  accounts: Account[];
  categories: Category[];
  /** All known tags for autocomplete suggestions */
  allTags?: string[];
  /** Tag stats with usage counts to display in suggestions */
  tagStats?: { tag: string; count: number }[];
  presentation?: UnifiedFilterBarPresentation;

  // [t-76e90519-CARRY] Optional slots carried verbatim from the shared dirty
  // UnifiedFilterBar so UnifiedTopBar's `expandedCount`/`expandedActions` props
  // type-check. See `/home/desendo/.../fintrack/frontend/src/components/
  // transactions/UnifiedFilterBar.tsx:162-163` for the dirty origin.
  expandedCount?: ReactNode;
  expandedActions?: ReactNode;
  /**
   * [AC7] Real available transaction date bounds, computed from the loaded
   * transaction list. Threaded through to TransactionDateRangeControl so the
   * All range can name its real Start/End dates instead of "All dates".
   * Optional; the control falls back to the placeholder when undefined.
   */
  transactionDateBounds?: { from?: Date; to?: Date };
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function UnifiedFilterBar({
  filters,
  onFiltersChange,
  onClear,
  accounts,
  categories,
  allTags = [],
  tagStats,
  presentation = 'full',
  expandedCount,
  expandedActions,
  transactionDateBounds,
}: UnifiedFilterBarProps) {

  const { toast } = useToast();
  const isMobile = useIsMobile();
  // [AC5] Ref to the owning transaction filter card. The popover uses this
  // card section's `left` + `width` so its calendar has usable room at narrow
  // widths while remaining symmetrically aligned with the card.
  const transactionFilterCardRef = useRef<HTMLDivElement>(null);
  const [expandedDateControlMetrics, setExpandedDateControlMetrics] = useState<
    { left: number; width: number } | undefined
  >(undefined);
  useEffect(() => {
    const filterCard = transactionFilterCardRef.current;
    if (!filterCard) return;
    const update = () => {
      const rect = filterCard.getBoundingClientRect();
      setExpandedDateControlMetrics({ left: rect.left, width: rect.width });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(filterCard);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false);
  // [T-WP2] customCalOpen + compactCalendarOpen removed; TransactionDateRangeControl owns its own popover state.
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [showSearchHelper, setShowSearchHelper] = useState(false);

  // [T114-F6-d] Undo ref for protected "Clear All"
  const undoFiltersRef = useRef<FilterState | null>(null);

  // [T231-PY] Container ref for the search-helper popover (toggle button +
  // absolute-positioned panel). Used by the tap-outside useEffect below so a
  // tap inside this ref is ignored while a tap on the table body / sidebar /
  // another component dismisses the popover (AC5). Mirrors the inline
  // mousedown pattern already used by tag-input.tsx:145-154 — kept local
  // because there is no shared useClickOutside hook in the codebase yet.
  const searchHelperContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPresets(loadPresets());
  }, []);

  // [T231-PY] Tap-outside dismissal for the search-helper popover. Uses
  // `pointerdown` (not `mousedown`) so single-finger taps on iOS Safari and
  // Android Chrome dispatch the same event we listen for — mousedown is
  // synthesized after pointerdown on touch devices but its relative ordering
  // with focus changes is browser-implementation-specific. The listener is
  // attached only while the popover is open (guarded by showSearchHelper)
  // so we don't pay the registration cost on a closed popover.
  useEffect(() => {
    if (!showSearchHelper) return;
    const handlePointerDown = (e: PointerEvent) => {
      const root = searchHelperContainerRef.current;
      if (root && !root.contains(e.target as Node)) {
        setShowSearchHelper(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showSearchHelper]);

  const chips = buildActiveChips(filters, accounts, categories);
  const hasActiveFilters = chips.length > 0 || filters.period !== '30d';

  // Count active advanced filters (for mobile badge)
  const advancedFilterCount = chips.length;

  // ── update helper ────────────────────────────────────────────────────────────
  const normalizeSearchValue = (raw: string): string | undefined => {
    const compact = raw.replace(/\s+/g, ' ').trim();
    return compact.length > 0 ? compact : undefined;
  };

  const toggleSearchHelper = useCallback(() => {
    setShowSearchHelper((prev) => !prev);
  }, []);

  const update = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // [T229] [T229-PY] Debounce universal-search updates so each keystroke does not
  // re-dispatch a transactions fetch through Redux, and skip no-op blur
  // normalization. Single root-cause fix: gate downstream cascade at the source.
  const SEARCH_DEBOUNCE_MS = 250;
// [T230-PY] Click-to-fill example chips for the search-helper popover. The first two
// items mirror the most common casual queries (implicit AND and quoted exact-phrase).
// The amount range (`rent > 500`) and date range (`2025-01..2025-12`) examples advertise
// backend-supported syntax (transactionHandler.ts:115-137) that wasn't previously surfaced.
// Backend treats space-separated tokens as implicit AND and quoted phrases as exact-match tokens;
// verified empirically at 1440x900 with the dev demo user (see T230-PY sign-off).
  const SEARCH_HELPER_EXAMPLES: readonly string[] = [
  'coffee shop',
  '"Coffee Shop"',
  'rent > 500',
  '2025-01..2025-12',
] as const;
  // [T230-PY-R2] CR-002: Replace the inline testid-derivation lambda with a stable
  // slug helper. The previous derivation (`replace(/\s+/g, '-').toLowerCase().replace(/[^\w-]/g, '')`)
  // produced `rent--500` for `'rent > 500'` (double dash) and `2025-012025-12` for
  // `'2025-01..2025-12'` (dots stripped, dashes lost). The new derivation collapses
  // runs of non-word-non-dash chars into single dashes and trims edges, producing
  // testid-friendly slugs that match the assertions in unified-topbar.spec.ts:241-242.
  const slugifyExample = (raw: string): string =>
    raw
      .trim()
      .replace(/\s+/g, '-')
      .replace(/([^\w-]|_)/g, (m) => (/^[a-z0-9]$/i.test(m) ? m : '-'))
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
// [T230-PY] searchDraftRef mirrors the local searchDraft state so handleSearchKeyDown
// can read the latest draft value when the Enter key fires (the React closure passed
// to the onKeyDown handler may otherwise capture a stale value). Mirrors the existing
// filtersRef pattern at line 297 — same useRef + useEffect-sync-from-props contract.
  const searchDraftRef = useRef<string>('');
// [T230-PY] suppressNextBlurRef: when Escape is pressed, handleSearchKeyDown sets the
// cleared state and then blurs the input. The synchronous blur handler reads the DOM
// input's `.value` (which is still the pre-Escape text "coffee" because React hasn't
// re-rendered yet). Without this flag, handleSearchBlur would re-commit the typed value
// and undo the Escape clear. Same pattern as the existing in-flight-debounce-cancel guard.
  const suppressNextBlurRef = useRef<boolean>(false);
// searchDraftRef.current is synced via the useEffect declared immediately after
// the searchDraft state initialization further down in this component (line ~393).
// Declaring the sync here would create a TDZ error because searchDraft is not
// yet defined at this point in the function body.
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const flushPendingSearch = (raw: string): boolean => {
    // Cancel any pending debounced update and commit `raw` normalized.
    // Returns true if a downstream `update` was actually called.
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const normalized = normalizeSearchValue(raw);
    if (normalized === filtersRef.current.search) return false;
    update('search', normalized);
    return true;
  };

  const handleSearchChange = (raw: string) => {
    setSearchDraft(raw); // immediate visual feedback regardless of debounce
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      // [T229-PY-R2] CR-002 defensive check: if the parent cleared filters.search
      // (e.g. via "Reset View" or "Clear All") between schedule and fire, do
      // NOT re-commit the user's typed value. The CR-002 useEffect ALSO cancels
      // the timer on incoming==='' transition, but this defensive check handles
      // the race where the timer's macrotask fires BEFORE the React effect cycle
      // processes the parent's state update (Playwright-driven reset clicks can
      // land in this window when the timer is scheduled ~250ms out).
      if (!filtersRef.current.search) return;
      const normalized = normalizeSearchValue(raw);
      if (normalized === filtersRef.current.search) return;
      update('search', normalized);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchBlur = (raw: string) => {
    // [T230-PY] Escape path suppresses this blur's commit so the cleared state sticks.
    // The flag is set inside handleSearchKeyDown right before `blur()` is called.
    if (suppressNextBlurRef.current) {
      suppressNextBlurRef.current = false;
      // Still sync the displayed text to '' in case the draft was somehow out of sync.
      setSearchDraft('');
      return;
    }
    // [T229-PY-R2] CR-003 + CR-004: synchronously sync the displayed text to the
    // normalized form BEFORE the parent re-render lands. This eliminates the
    // post-blur frame where the raw text shows briefly before the cascade returns
    // (visible as a 1-frame flicker when whitespace is collapsed, e.g.
    // "a  b  c" → "a b c"). The single source of truth — `normalizeSearchValue`
    // — is now applied to BOTH the local draft AND the parent filter in the same
    // synchronous tick.
    setSearchDraft(normalizeSearchValue(raw) ?? "");
    flushPendingSearch(raw); // commits immediately; skip-blur-when-unchanged built in
  };

  // [T230-PY] Keyboard commit + clear for the universal-search input.
  // Enter bypasses the 250ms debounce (commit synchronously via flushPendingSearch,
  // dismiss the helper popover, release focus). Escape clears the input + commits
  // the cleared state up + dismisses the popover (symmetric with Enter).
  // IME composition guard: ignore Enter while a CJK/multi-script IME is composing
  // — otherwise pressing Enter to confirm a candidate would prematurely commit.
  // [T230-PY-R2] CR-003: Extracted from the synthetic-event invocation pattern in the
  // chip click handler. Provides a single synchronous commit+dismiss path used by BOTH
  // the Enter keydown handler (with focus release) and the chip click handler (without
  // focus release — `<button type="button">` clicks shouldn't shift focus anyway).
  // Reuses the skip-blur-when-unchanged gate via flushPendingSearch so committing the
  // same value is a no-op (preserves T229-PY regression lock). Plain const (not
  // useCallback) because the only callers are event handlers fired at human speed —
  // memoization here would only add the eslint-disable tax with no perf win.
  const handleSearchCommit = (raw: string, opts?: { blurInput?: boolean }) => {
    flushPendingSearch(raw);
    setShowSearchHelper(false);
    if (
      opts?.blurInput &&
      typeof document !== 'undefined' &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchCommit(searchDraftRef.current ?? '', { blurInput: true });
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      // Mark the blur handler to skip the next commit — without this, the synchronous
      // blur fired by `document.activeElement.blur()` would re-commit the typed value
      // (because the input's DOM `.value` hasn't been re-rendered with the cleared draft
      // yet) and undo the Escape clear.
      suppressNextBlurRef.current = true;
      setSearchDraft('');
      update('search', undefined);
      setShowSearchHelper(false);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // [T229] Mirror `filters.search` into a local draft so the controlled input
  // updates instantly while the debounced parent update is in flight. This also
  // lets external resets (e.g., Clear All) override the input immediately.
  const [searchDraft, setSearchDraft] = useState<string>(filters.search ?? '');
  // [T230-PY] Mirror searchDraft into searchDraftRef so handleSearchKeyDown reads the
  // latest value when Enter fires (the closure captured by onKeyDown may be stale).
  useEffect(() => { searchDraftRef.current = searchDraft; }, [searchDraft]);
  useEffect(() => {
    // Only sync from props when the parent's normalized form differs from the
    // current draft. This keeps the user's in-flight typing intact mid-debounce.
    const incoming = filters.search ?? '';
    // [T229-PY-R2] CR-002 — Clear-All race guard. If the parent transitions
    // filters.search to undefined/empty (e.g. via the "Reset View" or "Clear
    // All" affordance), cancel any in-flight debounced search so the timer
    // cannot fire AFTER the reset and re-commit the user's typed value,
    // undoing the reset. Without this guard, the sequence:
    //   1. user types "abc" → debounce scheduled for t+250ms
    //   2. user clicks Clear All before 250ms → filters.search → undefined
    //   3. timer fires at t+250ms → update('search', 'abc') undoing Clear All
    // re-introduces the exact UX glitch this v3.15 fix was meant to eliminate.
    if (incoming === '') {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      setSearchDraft('');
      return;
    }
    if (searchDebounceRef.current === null && incoming !== searchDraft) {
      setSearchDraft(incoming);
    }
    // We intentionally do NOT depend on `searchDraft` to avoid feedback loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  // ── Preset handlers ───────────────────────────────────────────────────────────
  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    if (presets.length >= MAX_PRESETS) return; // [T114-F6-g] enforce max
    const newPreset: FilterPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      filters,
    };
    const next = [...presets, newPreset];
    setPresets(next);
    savePresets(next);
    setPresetName('');
    setPresetPopoverOpen(false);
  };

  const handleDeletePreset = (id: string) => {
    const next = presets.filter((p) => p.id !== id);
    setPresets(next);
    savePresets(next);
    if (activePresetId === id) setActivePresetId(null);
  };

  const handleApplyPreset = (preset: FilterPreset) => {
    // Re-hydrate Date objects (localStorage serialises to ISO strings)
    const f: FilterState = { ...preset.filters };
    if (f.dateFrom) f.dateFrom = new Date(f.dateFrom);
    if (f.dateTo) f.dateTo = new Date(f.dateTo);
    onFiltersChange(f);
    setActivePresetId(preset.id);
    setPresetPopoverOpen(false);
  };

  // [T114-F6-d] Protected clear-all with undo toast
  const handleProtectedClear = useCallback(() => {
    undoFiltersRef.current = { ...filters };
    onClear();
    setActivePresetId(null);
    toast({
      title: 'Filters cleared',
      description: 'All filters have been reset to defaults.',
      duration: 5000,
      action: (
        <ToastAction
          altText="Undo clear filters"
          onClick={() => {
            if (undoFiltersRef.current) {
              onFiltersChange(undoFiltersRef.current);
              undoFiltersRef.current = null;
            }
          }}
        >
          Undo
        </ToastAction>
      ),
    });
  }, [filters, onClear, onFiltersChange, toast]);

  // [T125-F5-a] Period change handler with undo protection for custom date ranges
  // [T125-F5-b] Also clears stale dateFrom/dateTo when switching away from custom
  const handlePeriodChange = useCallback((newPeriod: FilterState['period']) => {
    const hadCustomDates = filters.period === 'custom' && (filters.dateFrom || filters.dateTo);
    if (hadCustomDates) {
      undoFiltersRef.current = { ...filters };
    }
    const next: FilterState = { ...filters, period: newPeriod };
    if (newPeriod !== 'custom') {
      delete next.dateFrom;
      delete next.dateTo;
    }
    onFiltersChange(next);
    setActivePresetId(null);
    if (hadCustomDates) {
      toast({
        title: 'Custom date range replaced',
        description: `Switched to ${PERIOD_LABELS[newPeriod]} preset`,
        duration: 5000,
        action: (
          <ToastAction
            altText="Undo"
            onClick={() => {
              if (undoFiltersRef.current) {
                onFiltersChange(undoFiltersRef.current);
                undoFiltersRef.current = null;
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    }
  }, [filters, onFiltersChange, toast]);

  // [T114-F6-e] Chip text click → open/focus the corresponding filter control
  const handleChipEdit = useCallback((chipKey: string) => {
    const field = chipKeyToAdvancedField(chipKey);
    if (field) {
      if (isMobile) {
        setMobileSheetOpen(true);
      } else if (!showAdvanced) {
        setShowAdvanced(true);
      }
    }
  }, [isMobile, showAdvanced]);
  // [T-WP2] Date range logic (dateRangeValue, handleDateRangeSelect, handleCompactDateRangeSelect, compactDateLabel) extracted to TransactionDateRangeControl. The control owns its own popover draft state and Apply-only commit semantics; preset commits route through handlePeriodChange below to preserve the custom→preset undo toast.

  // ── Advanced filter controls (shared between desktop inline and mobile sheet) ──
  const advancedFilterControls = (
    <div className="grid gap-3 pt-2 border-t grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="advanced-filters-panel">
      {/* Type */}
      <div>
        <span className="text-xs font-medium text-muted-foreground mb-1.5 block">Type</span>
        <div className="flex gap-1">
          {(['income', 'expense', 'transfer'] as const).map((t) => (
            <Button
              key={t}
              variant={filters.type === t ? 'default' : 'outline'}
              size="sm"
              className="h-11 min-h-[44px] md:h-8 md:min-h-0 px-2 text-xs font-medium flex-1"
              onClick={() => update('type', filters.type === t ? undefined : t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Account */}
      <div>
        <span className="text-xs font-medium text-muted-foreground mb-1.5 block">Account</span>
        <Select
          value={filters.accountIds?.[0] ?? 'all'}
          onValueChange={(v) => update('accountIds', v === 'all' ? undefined : [v])}
        >
          <SelectTrigger className="h-11 min-h-[44px] md:h-8 md:min-h-0 text-xs">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            {accounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.emoji ? `${acc.emoji} ` : ''}{acc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Category */}
      <div>
        <span className="text-xs font-medium text-muted-foreground mb-1.5 block">Category</span>
        <Select
          value={filters.categoryIds?.[0] ?? 'all'}
          onValueChange={(v) => update('categoryIds', v === 'all' ? undefined : [v])}
        >
          <SelectTrigger className="h-11 min-h-[44px] md:h-8 md:min-h-0 text-xs">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Amount range */}
      <div>
        <span className="text-xs font-medium text-muted-foreground mb-1.5 block">Amount range</span>
        <div className="flex gap-1">
          <Input
            type="number"
            placeholder="Min"
            value={filters.amountMin ?? ''}
            onChange={(e) => update('amountMin', e.target.value ? Number(e.target.value) : undefined)}
            className="h-11 min-h-[44px] md:h-8 md:min-h-0 text-base md:text-xs"
            min={0}
          />
          <Input
            type="number"
            placeholder="Max"
            value={filters.amountMax ?? ''}
            onChange={(e) => update('amountMax', e.target.value ? Number(e.target.value) : undefined)}
            className="h-11 min-h-[44px] md:h-8 md:min-h-0 text-base md:text-xs"
            min={0}
          />
        </div>
      </div>

      {/* Tags */}
      <div className="w-full sm:col-span-2 lg:col-span-3" data-testid="advanced-tags-filter">
        <span className="text-xs font-medium text-muted-foreground mb-1.5 block">Tags</span>
        <TagInput
          value={filters.tags ?? []}
          onChange={(tags) => update('tags', tags.length ? tags : undefined)}
          suggestions={allTags}
          tagStats={tagStats}
          placeholder="Add tag…"
          compact
          className="w-full min-h-[44px] md:min-h-0"
        />
      </div>

      {/* Status */}
      <div>
        <span className="text-xs font-medium text-muted-foreground mb-1.5 block">Status</span>
        <div className="flex gap-1">
          {(['pending', 'cleared', 'reconciled'] as const).map((s) => (
            <Button
              key={s}
              variant={filters.status === s ? 'default' : 'outline'}
              size="sm"
              className="h-11 min-h-[44px] md:h-8 md:min-h-0 px-2 text-xs font-medium flex-1"
              onClick={() => update('status', filters.status === s ? undefined : s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Recurring toggle */}
      <div className="flex items-center gap-2 pt-1 min-h-[44px] md:min-h-0">
        <Switch
          id="filter-recurring"
          checked={filters.isRecurring ?? false}
          onCheckedChange={(checked) => update('isRecurring', checked ? true : undefined)}
        />
        <Label htmlFor="filter-recurring" className="text-xs cursor-pointer min-h-[44px] md:min-h-0 inline-flex items-center">
          Recurring only
        </Label>
      </div>
    </div>
  );

  if (presentation === 'minimized-inline') {
    return (
      <div data-testid="minimized-filter-controls" className="contents">
        <TransactionDateRangeControl
          value={filters}
          onCommit={onFiltersChange}
          onPresetCommit={handlePeriodChange}
          presentation="minimized"
          transactionDateBounds={transactionDateBounds}
        />


        <div ref={searchHelperContainerRef} data-testid="minimized-search-container" className="relative z-0 min-w-[44px] flex-1 focus-within:z-10">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="universal-search-input"
            placeholder="Search"
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
            onBlur={(e) => handleSearchBlur(e.target.value)}
            aria-describedby={showSearchHelper ? 'search-helper-panel' : undefined}
            onKeyDown={(e) => handleSearchKeyDown(e)}
            enterKeyHint="search"
            aria-label="Search transactions"
            className="h-11 min-h-[44px] min-w-0 w-full pl-7 pr-1 text-[10px] focus-visible:ring-inset focus-visible:ring-offset-0"
          />
          <button
            type="button"
            className={cn(
              "absolute right-0 top-1/2 h-11 w-11 min-h-[44px] min-w-[44px] -translate-y-1/2 rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:ring-offset-0",
              presentation === 'minimized-inline' && "hidden",
            )}
            onClick={toggleSearchHelper}
            aria-label={showSearchHelper ? 'Hide search guidance' : 'Show search guidance'}
            aria-expanded={showSearchHelper}
            aria-controls="search-helper-panel"
            data-testid="search-helper-toggle"
          >
            <CircleHelp className="mx-auto h-4 w-4" />
          </button>
          <div
            id="search-helper-panel"
            role="region"
            aria-label="Search guidance"
            aria-live="polite"
            aria-hidden={!showSearchHelper}
            hidden={!showSearchHelper}
            className={cn(
              'absolute left-0 top-[calc(100%_+_0.35rem)] z-20 w-[min(20rem,calc(100vw_-_2.5rem))] rounded-md border bg-popover text-popover-foreground shadow-lg',
              'touch-pan-y max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain',
              'transition-[opacity,transform] duration-150',
              // [WP7] Closed state uses display:none (Tailwind `hidden`) instead of visibility:hidden. visibility:hidden preserves the layout box, so the 320px-wide absolute child still inflated #floating-bar-actions.scrollWidth from 390 to 464 at 390x844 (proven by direct served probe). display:none removes the element from layout/scrollable overflow entirely while preserving the open panel DOM, role, data-testid, aria-hidden={!showSearchHelper}, aria-controls, and pointer behavior. The full expanded helper keeps its intentional `pointer-events-none invisible ...` strategy because its parent is not overflow-x-auto.

              showSearchHelper ? 'translate-y-0 opacity-100' : 'hidden',

            )}
          >
            <div className="border-b px-3 py-2">
              <p className="text-sm text-foreground" data-testid="search-helper-hint">
                <span className="font-semibold">Search tips</span>
                <span className="mx-1.5 text-muted-foreground">·</span>
                <span className="font-medium">Space = AND</span>
              </p>
            </div>
            <div className="space-y-1.5 px-3 py-2.5" data-testid="search-helper-examples">
              {SEARCH_HELPER_EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => {
                    setSearchDraft(example);
                    handleSearchChange(example);
                    handleSearchCommit(example);
                  }}
                  className="block w-full min-h-[44px] rounded-md border border-dashed px-3 py-2 text-left text-sm transition-colors hover:border-primary hover:bg-accent break-words"
                  data-testid={`search-helper-example-${slugifyExample(example)}`}
                >
                  {example}
                </button>
              ))}
            </div>
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[0.7rem]">Enter</kbd>
              <span className="ml-1.5">to search</span>
              <span className="mx-2">·</span>
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[0.7rem]">Esc</kbd>
              <span className="ml-1.5">to clear</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={transactionFilterCardRef} data-testid="transaction-filter-card" className="rounded-xl border bg-card shadow-none transition-none p-3 space-y-2">
      {/* [T114-F6-g] Row 0: Saved preset quick-apply chips */}
      {presets.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs cursor-pointer transition-colors',
                activePresetId === preset.id
                  ? 'border border-primary bg-primary/10 text-primary'
                  : 'border border-dashed hover:border-primary',
              )}
              onClick={() => handleApplyPreset(preset)}
            >
              <Star className="h-3 w-3" />
              {preset.name}
            </button>
          ))}
        </div>
      )}

      {/* Row 1: Date range control + right controls */}
      {/* Row 1: Date range control + right controls. The owning card feeds
          its metrics into the popover so the surface aligns with this section. */}
      <div
        data-testid="expanded-date-actions-row"
        className="flex flex-nowrap items-center gap-1.5"
      >
        {/* [T-WP2] Unified date range trigger + popover (period pills now live inside the popover) */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5 py-1">
          {expandedCount}
          <div className="min-w-0 flex-1">
            <TransactionDateRangeControl
              value={filters}
              onCommit={onFiltersChange}
              onPresetCommit={handlePeriodChange}
              presentation="expanded"
              transactionDateBounds={transactionDateBounds}
              popoverContainerMetrics={expandedDateControlMetrics}
            />
          </div>
          {expandedActions}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Save/load presets */}
          <Popover open={presetPopoverOpen} onOpenChange={setPresetPopoverOpen}>
            <PopoverTrigger asChild>
              <IconButton
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                tooltip="Saved filter presets"
                tooltipSide="bottom"
              >
                <Star className="h-3.5 w-3.5" />
              </IconButton>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <p className="text-sm font-medium mb-3">Filter Presets</p>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Preset name…"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  className="h-8 text-base md:text-xs"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
                  disabled={presets.length >= MAX_PRESETS}
                />
                <Button
                  size="sm"
                  className="h-8 text-xs shrink-0"
                  onClick={handleSavePreset}
                  disabled={presets.length >= MAX_PRESETS}
                >
                  Save
                </Button>
              </div>
              {presets.length >= MAX_PRESETS && (
                <p className="text-xs text-amber-600 mb-2">Maximum {MAX_PRESETS} presets reached. Delete one to save a new preset.</p>
              )}
              {presets.length === 0 && (
                <p className="text-xs text-muted-foreground">No saved presets yet.</p>
              )}
              <div className="touch-pan-y overscroll-contain md:[scrollbar-gutter:stable] space-y-1 max-h-48 overflow-y-auto">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60 cursor-pointer"
                    onClick={() => handleApplyPreset(preset)}
                  >
                    <span className="text-sm truncate">{preset.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleDeletePreset(preset.id); }}
                      aria-label={`Delete preset ${preset.name}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* [T114-F6-d] Clear all — protected with undo */}
          {hasActiveFilters && (
            <IconButton
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              tooltip="Clear all filters"
              tooltipSide="bottom"
              onClick={handleProtectedClear}
            >
              <X className="h-3.5 w-3.5" />
            </IconButton>
          )}
        </div>
      </div>

      {/* Row 2: Universal Search + Unique Filter Action */}
      <div className="flex items-start gap-2">
        <div ref={searchHelperContainerRef} className="relative z-0 flex-1 min-w-0 focus-within:z-10">
          <Search className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="universal-search-input"
            placeholder="Search transactions and items..."
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
            onBlur={(e) => handleSearchBlur(e.target.value)}
            aria-describedby={showSearchHelper ? 'search-helper-panel' : undefined}
            onKeyDown={(e) => handleSearchKeyDown(e)}
            enterKeyHint="search"
            aria-label="Search transactions"
            className="h-9 min-h-[44px] pl-9 pr-12 md:min-h-0 md:pr-10 text-base md:text-xs focus-visible:ring-inset focus-visible:ring-offset-0"
          />

          <button
            type="button"
            className="absolute right-0 top-1/2 h-9 w-11 min-h-[44px] min-w-[44px] -translate-y-1/2 rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:ring-offset-0 md:h-7 md:w-8 md:min-h-0 md:min-w-0"
            onClick={toggleSearchHelper}
            aria-label={showSearchHelper ? 'Hide search guidance' : 'Show search guidance'}
            aria-expanded={showSearchHelper}
            aria-controls="search-helper-panel"
            data-testid="search-helper-toggle"
          >
            <CircleHelp className="mx-auto h-4 w-4" />
          </button>

          <div
            id="search-helper-panel"
            role="region"
            aria-label="Search guidance"
            aria-live="polite"
            aria-hidden={!showSearchHelper}
            data-testid="search-helper-panel"
            className={cn(
              // [T231-PY] Mobile fix: switch the horizontal anchor from `right-0`
              // (which made the panel extend left past the viewport at <375px because
              // the search-input parent is narrower than the popover at narrow
              // viewports — observed panel.x=-77.7 at 320x568) to `left-0` on mobile,
              // restoring `right-0` only at md+ where the parent is wide enough to
              // contain the popover without overflow.
              'absolute left-0 md:left-auto md:right-0 top-[calc(100%_+_0.35rem)] z-20 w-[min(20rem,calc(100vw_-_2.5rem))] rounded-md border bg-popover text-popover-foreground shadow-lg',
              // [T231-PY] Mobile fix: cap the panel height to the dynamic viewport
              // (100dvh shrinks when the iOS Safari URL bar collapses, so the chip
              // + kbd footer stay reachable). 4.5rem reserves ~search-input row +
              // sticky wrapper offset so the popover doesn't push off the top
              // either. overscroll-contain prevents the body scroll from being
              // chained when the user reaches the popover scroll boundary.
              'touch-pan-y md:[scrollbar-gutter:stable] max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain',
              'transition-[opacity,transform] duration-150',
              showSearchHelper ? 'translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-1 opacity-0',
            )}
          >
            {/* Header */}
            {/* [T230-PY-R3] CR-001 final: Merged the syntax literal INTO the helperHint
                element so the existing /Space = AND/i assertions (transactions.spec.ts:666 + :2364)
                AND the /Search tips/i assertion (unified-topbar.spec.ts:234) all pass on the
                SAME element. The R2 sibling <p data-testid="search-helper-syntax-summary">
                was redundant because helperHint.toContainText operates on the matched
                element's text content only — siblings don't count. */}
            <div className="border-b px-3 py-2">
              <p
                className="text-sm text-foreground"
                data-testid="search-helper-hint"
              >
                <span className="font-semibold">Search tips</span>
                <span className="mx-1.5 text-muted-foreground">·</span>
                <span className="font-medium">Space = AND</span>
                <span className="ml-1.5 text-muted-foreground">
                  Wrap in <span className="font-medium">&quot;quotes&quot;</span> for exact phrase matching
                </span>
              </p>
            </div>

            {/* Body: click-to-fill example chips */}
            <div className="space-y-1.5 px-3 py-2.5" data-testid="search-helper-examples">
              {SEARCH_HELPER_EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  // [T230-PY-R2] CR-003: Replaced the synthetic-KeyboardEvent invocation with
                  // a direct call to handleSearchCommit. The chip click does NOT request focus
                  // release — `<button type="button">` doesn't shift focus anyway, and the
                  // test assertion `expect(search).not.toBeFocused()` was browser-implementation-
                  // specific.
                  onClick={() => {
                    setSearchDraft(example);
                    handleSearchChange(example);
                    handleSearchCommit(example);
                  }}
                  className="block w-full min-h-[44px] rounded-md border border-dashed px-3 py-2 text-left text-sm transition-colors hover:border-primary hover:bg-accent break-words"
                  // [T230-PY-R2] CR-002: Use the slugifyExample helper for stable testids.
                  data-testid={`search-helper-example-${slugifyExample(example)}`}
                >
                  {example}
                </button>
              ))}
            </div>

            {/* Footer: keyboard hints */}
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[0.7rem]">Enter</kbd>
              <span className="ml-1.5">to search</span>
              <span className="mx-2">·</span>
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[0.7rem]">Esc</kbd>
              <span className="ml-1.5">to clear</span>
              <span className="mx-2">·</span>
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[0.7rem]">/</kbd>
              <span className="ml-1.5">to focus</span>
            </div>
          </div>
        </div>

        {isMobile ? (
          <Button
            variant={mobileSheetOpen ? 'secondary' : 'outline'}
            size="sm"
            className="h-9 min-h-[44px] px-3 shrink-0"
            data-testid="advanced-filters-btn"
            aria-label="Advanced filters"
            onClick={() => setMobileSheetOpen(true)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span className="ml-1 text-xs">Filter</span>
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="h-4 min-w-4 px-1 text-[10px] font-semibold ml-1">
                {advancedFilterCount}
              </Badge>
            )}
          </Button>
        ) : (
          <Button
            variant={advancedFilterCount > 0 || showAdvanced ? 'secondary' : 'outline'}
            size="sm"
            className="h-9 px-3 text-xs shrink-0"
            onClick={() => setShowAdvanced(!showAdvanced)}
            data-testid="advanced-filters-btn"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span className="ml-1">Filter</span>
            {advancedFilterCount > 0 && (
              <Badge variant="default" className="h-4 min-w-4 px-1 text-[10px] font-semibold rounded-full ml-1">
                {advancedFilterCount}
              </Badge>
            )}
            {showAdvanced
              ? <ChevronUp className="h-3 w-3 opacity-50 ml-1" />
              : <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
            }
          </Button>
        )}
      </div>

      {/* [T114-F6-e] Row 3: Enhanced active filter chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center rounded-full border bg-muted/60 text-xs font-medium text-foreground group"
            >
              {/* Text area — clicking opens/focuses the corresponding filter control */}
              <span
                role="button"
                tabIndex={0}
                aria-label={`Edit filter: ${chip.label}`}
                className="pl-2.5 py-0.5 cursor-pointer hover:text-primary transition-colors"
                onClick={() => handleChipEdit(chip.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChipEdit(chip.key); } }}
              >
                {chip.label}
              </span>
              {/* × button — removes the filter */}
              <button
                className="ml-1 pr-2 py-0.5 hover:text-destructive focus:outline-none"
                onClick={() => removeChip(chip.key, filters, onFiltersChange)}
                aria-label={`Remove filter: ${chip.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* [T114-F6-f] Mobile bottom sheet for advanced filters */}
      {isMobile && (
        <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
          <SheetContent side="bottom" className="max-h-[85dvh] flex flex-col p-0">
            <SheetHeader className="px-4 pt-4 pb-2">
              <SheetTitle>Advanced Filters</SheetTitle>
              <SheetDescription>Narrow down your transaction list</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {advancedFilterControls}
            </div>
            <SheetFooter className="sticky bottom-0 border-t bg-background px-4 py-3 flex-row gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-11 min-h-[44px]"
                onClick={() => { onClear(); setMobileSheetOpen(false); }}
              >
                Reset
              </Button>
              <Button
                size="sm"
                className="flex-1 h-11 min-h-[44px]"
                onClick={() => setMobileSheetOpen(false)}
              >
                Apply
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      )}

      {/* Desktop: Advanced drawer — layout changes immediately; opacity transition only */}
      {!isMobile && (
        <div
          data-testid="advanced-filters-drawer"
          className="transition-opacity duration-300 ease-in-out"
          hidden={!showAdvanced}
        >
          <div className="overflow-hidden min-h-0">
            {advancedFilterControls}
          </div>
        </div>
      )}
    </div>
  );
}
